// App shell: a collapsible left sidebar (nav, filtered by the user's
// permissions, with click-to-expand groups mirroring the PHP nav) + a
// topbar (sidebar toggle, brand, poller-status widget, current user,
// theme, sign out) + the routed content. The sidebar's open/closed state
// persists in localStorage.

import { Suspense, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronDown, PanelLeft, PanelLeftClose } from "lucide-react";
import { Button, Spinner, cn } from "@noc/ui";
import { useAuth } from "@/features/auth/AuthContext";
import { ThemeToggle } from "./ThemeToggle";
import { PollerWidget, RestartDaemonButton } from "./PollerWidget";
import { MessengerBubble } from "@/features/messenger/MessengerBubble";
import { useVisibleNav, type NavItem } from "./nav";

const SIDEBAR_KEY = "noc.sidebar";

function readSidebarOpen(): boolean {
  try {
    const s = localStorage.getItem(SIDEBAR_KEY);
    if (s != null) return s !== "0";
  } catch {
    /* ignore */
  }
  return typeof window !== "undefined" ? window.innerWidth >= 1024 : true;
}

function pathMatches(pathname: string, to: string, end?: boolean): boolean {
  if (to === "/") return pathname === "/";
  if (end) return pathname === to;
  return pathname === to || pathname.startsWith(to + "/");
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(readSidebarOpen);
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, sidebarOpen ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [sidebarOpen]);

  return (
    <div className="flex h-full flex-col">
      {/* Topbar */}
      <header className="flex items-center justify-between gap-3 border-b bg-card px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-label={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
            title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
          >
            {sidebarOpen ? <PanelLeftClose /> : <PanelLeft />}
          </Button>
          <div className="flex items-center gap-2">
            <span className="inline-block size-2 rounded-full bg-primary" />
            <span className="text-sm font-semibold">NOC Portal</span>
            <span className="ml-1 rounded bg-warning/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-warning-foreground dark:text-warning">
              beta
            </span>
          </div>
          <PollerWidget />
          <RestartDaemonButton />
        </div>
        <div className="flex items-center gap-3 text-sm">
          {user && (
            <span className="hidden text-muted-foreground sm:inline">
              {user.full_name || user.username}{" "}
              <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                {user.role}
              </span>
            </span>
          )}
          <ThemeToggle />
          <Button size="sm" variant="outline" onClick={() => logout()}>
            Sign out
          </Button>
        </div>
      </header>

      {/* Body: sidebar + content */}
      <div className="flex flex-1 overflow-hidden">
        <aside
          className={cn(
            "shrink-0 overflow-hidden border-r bg-card transition-[width] duration-200 ease-out",
            sidebarOpen ? "w-56" : "w-0",
          )}
        >
          <div className="h-full w-56 overflow-y-auto">
            <SidebarNav />
          </div>
        </aside>
        <main className="flex-1 overflow-auto bg-background p-4 sm:p-6">
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-16">
                <Spinner />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Intercom-style social-inbox bubble — fixed-position; renders
          nothing unless the user can use the inbox (messenger:reply). */}
      <MessengerBubble />
    </div>
  );
}

function SidebarNav() {
  const nav = useVisibleNav();
  const { pathname } = useLocation();
  return (
    <nav className="space-y-0.5 p-3">
      {nav.map((item) =>
        item.children && item.children.length > 0 ? (
          <NavGroup key={item.label} item={item} pathname={pathname} />
        ) : (
          <NavRow key={item.label} item={item} />
        ),
      )}
    </nav>
  );
}

// A collapsible group (e.g. OLTs → OLT Issues). The header row is a
// toggle; the parent's own page (if any) appears as the first child link
// ("All <label>") — mirrors the PHP nav. Auto-expands when the active
// route is inside it.
function NavGroup({ item, pathname }: { item: NavItem; pathname: string }) {
  const containsActive =
    (item.to != null && pathMatches(pathname, item.to, item.end)) ||
    (item.children ?? []).some((c) => c.to != null && pathMatches(pathname, c.to, c.end));
  const [open, setOpen] = useState(containsActive);
  useEffect(() => {
    if (containsActive) setOpen(true);
  }, [containsActive]);
  const Icon = item.icon;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
          containsActive
            ? "font-medium text-foreground"
            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        )}
        aria-expanded={open}
      >
        <Icon className="size-4 shrink-0" />
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDown className={cn("size-3.5 shrink-0 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="mt-0.5 space-y-0.5">
          {item.to != null && (
            <NavRow item={{ label: `All ${item.label}`, to: item.to, end: item.end, icon: item.icon }} indent />
          )}
          {(item.children ?? []).map((c) => (
            <NavRow key={c.label} item={c} indent />
          ))}
        </div>
      )}
    </div>
  );
}

function NavRow({ item, indent }: { item: NavItem; indent?: boolean }) {
  const Icon = item.icon;
  if (!item.to) {
    // Children-only group header (no own page) — a plain label.
    return (
      <div className="flex items-center gap-2 px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="size-4 shrink-0" />
        {item.label}
      </div>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-2 rounded-md py-2 text-sm transition-colors",
          indent ? "pl-9 pr-3" : "px-3",
          isActive
            ? "bg-primary/10 font-medium text-primary"
            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        )
      }
    >
      <Icon className={cn("shrink-0", indent ? "size-3.5" : "size-4")} />
      {item.label}
    </NavLink>
  );
}
